import React, { useState } from 'react';
import styles from '../assets/styles/ContactUsSection.module.css';
import ContactDetailLabelBody from './ContactDetailLabelBody';
import { FaPhoneAlt, FaMapMarkerAlt } from "react-icons/fa";
import { MdEmail } from "react-icons/md";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const ContactUsSection = () => {
  const [inputName, setName] = useState('');
  const [inputPhone, setPhone] = useState('');
  const [inputEmail, setEmail] = useState('');
  const [selectedLaw, setSelectedLaw] = useState('');
  const [inputMessage, setMessage] = useState('');
  const [errors, setErrors] = useState({});
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);

  // Helper function to validate the form fields
  const validateForm = (name, phone, email) => {
    const newErrors = {};
    if (!name.trim()) {
      newErrors.name = "Name is required.";
    }
    if (!phone.trim()) {
      newErrors.phone = "Phone is required.";
    } else if (!/^[0-9+\s()-]{7,}$/.test(phone)) {
      newErrors.phone = "Please enter a valid phone number.";
    }
    if (!email.trim()) {
      newErrors.email = "Email is required.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = "Please enter a valid email address.";
    }
    return newErrors;
  };

  const handleNameChange = (e) => {
    const value = e.target.value;
    setName(value);
    if (hasSubmitted) {
      const nameError = value.trim() ? undefined : "Name is required.";
      setErrors(prev => ({ ...prev, name: nameError }));
    }
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value;
    setPhone(value);
    if (hasSubmitted) {
      let phoneError;
      if (!value.trim()) {
        phoneError = "Phone is required.";
      } else if (!/^[0-9+\s()-]{7,}$/.test(value)) {
        phoneError = "Please enter a valid phone number.";
      } else {
        phoneError = undefined;
      }
      setErrors(prev => ({ ...prev, phone: phoneError }));
    }
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    if (hasSubmitted) {
      let emailError;
      if (!value.trim()) {
        emailError = "Email is required.";
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        emailError = "Please enter a valid email address.";
      } else {
        emailError = undefined;
      }
      setErrors(prev => ({ ...prev, email: emailError }));
    }
  };

  const handleLawChange = (e) => {
    setSelectedLaw(e.target.value);
  };

  const handleMessageChange = (e) => {
    setMessage(e.target.value);
  };

  const handleSubmitClicked = async () => {
    setHasSubmitted(true);
    const newErrors = validateForm(inputName, inputPhone, inputEmail);
  
    if (Object.keys(newErrors).length) {
      setErrors(newErrors);
      return;
    }
    setErrors({});

    try {
      await fetch("https://prod-09.australiasoutheast.logic.azure.com:443/workflows/9368cc73066f4ae5907b540107fea69c/triggers/When_a_HTTP_request_is_received/paths/invoke?api-version=2016-10-01&sp=%2Ftriggers%2FWhen_a_HTTP_request_is_received%2Frun&sv=1.0&sig=7eQ9wjjvhwLhlyVRU2Jlfi1hVuvpRhTSAWn3PF2J_DM", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          name: inputName,
          phone: inputPhone,
          email: inputEmail,
          areaOfLaw: selectedLaw,
          message: inputMessage 
        }),
      });
      setFormSubmitted(true);
      toast.success("Form submitted successfully! We will get back to you shortly.");
    } catch (error) {
      toast.warning("Something went wrong while submitting the form. Please contact us directly.");
    }
  };

  return (
    <div>
      <div className={styles.contactUsSection}>
        <div className={styles.title}>Book your free in-person consultation today.</div>
        <div className={styles.formContainer}>
          <div className={styles.infoContainer}>
            <div className={styles.infoText}>
              Send us a message or call us during office hours. For urgent matters only we are available 24/7.
            </div>
            <div className={styles.contactDetailLabels}>
              <ContactDetailLabelBody href="tel:+61 416 185 595" icon={<FaPhoneAlt />} text="0416 185 595 (Kimani)" />
              <ContactDetailLabelBody href="tel:+61 424 441 114" icon={<FaPhoneAlt />} text="0424 441 114 (Yusef)" />
              <ContactDetailLabelBody href="mailto:info@starnetlegal.com.au" icon={<MdEmail />} text="info@starnetlegal.com.au" />
              <ContactDetailLabelBody href="https://maps.app.goo.gl/6rDsfa3JTP3NbaZs7" icon={<FaMapMarkerAlt />} text="Suite 4, 1004 Doncaster Road, Doncaster East, VIC 3109" />
            </div>
          </div>
          <div className={styles.form}>
            <div className={styles.formControls}>
              <div>
                <label className={styles.controlLabel}>Name (required)</label>
                <input
                  type="text"
                  className={styles.formField}
                  value={inputName}
                  onChange={handleNameChange}
                  disabled={formSubmitted}
                />
                {hasSubmitted && errors.name && <p className={styles.error}>{errors.name}</p>}
              </div>
              <div>
                <label className={styles.controlLabel}>Phone (required)</label>
                <input 
                  type="text" 
                  className={styles.formField} 
                  value={inputPhone} 
                  onChange={handlePhoneChange} 
                  disabled={formSubmitted}
                />
                {hasSubmitted && errors.phone && <p className={styles.error}>{errors.phone}</p>}
              </div>
              <div>
                <label className={styles.controlLabel}>Email (required)</label>
                <input
                  type="text"
                  className={styles.formField}
                  value={inputEmail}
                  onChange={handleEmailChange}
                  disabled={formSubmitted}
                />
                {hasSubmitted && errors.email && <p className={styles.error}>{errors.email}</p>}
              </div>
              <div>
                <label className={styles.controlLabel}>Area of Law</label>
                <select 
                  className={styles.formField}
                  value={selectedLaw}
                  onChange={handleLawChange}
                  disabled={formSubmitted}
                >
                  <option value="">Select an option</option>
                  <option value="Criminal Law">Criminal Law</option>
                  <option value="Civil & Commercial Litigation">Civil & Commercial Litigation</option>
                  <option value="Family Law">Family Law</option>
                  <option value="Immigration Law">Immigration Law</option>
                </select>
              </div>
              <div>
                <label className={styles.controlLabel}>Message</label>
                <textarea
                  rows="5"
                  className={`${styles.formField} ${styles.textArea}`}
                  value={inputMessage}
                  onChange={handleMessageChange}
                  placeholder="Please give us a brief description of your legal issue. This is optional, but the more information you include here, the better prepared we can be for our consultation with you."
                  disabled={formSubmitted}
                />
              </div>
            </div>
            <div 
              className={styles.submitButton} 
              onClick={formSubmitted ? null : handleSubmitClicked}
              style={formSubmitted ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
            >
              Submit
            </div>
          </div>
        </div>
      </div>
      <ToastContainer />
    </div>
  );
};

export default ContactUsSection;
